import d3 from 'd3';
import moment from 'moment';

/**
 * The map service is a singleton that is used on the map module taht keeps track of the current context of the map as well as helper functions
 * The map service must NOT contain any div or layout related variables and is intended to help organize the context variables of the scope...such that they can be accessed throughout.
 */
export default function MapService(
  $rootScope,
  gettextCatalog,
  localStorageService,
  $location,
  usersService,
) {
  'ngInject';

  const addToDataLayer = (data) => window.dataLayer.push(data);

  const self = this;

  self.filters = [];
  self.queryString = '*';
  self.visibleGeozones = [];
  self.selectedGeozone = {};
  self.selectedEquipment = null;
  self.selectedEquipmentFull = null;
  self.currentBounds = null;

  self.equipmentResponseData = null;

  self.cursor = 0;
  self.requestedCursor = 0;

  self.loading = false;
  self.assetLoading = false;

  self.facets = null;
  self.tagFilters = null;

  self.controlLayer = null;

  self.isMapLoaded = false; // too many watches calling map before map can load

  self.requestedEquipmentId = null;

  self.SEARCH_MODE = {
    IN_MAP_BOUNDARY: 'in-map-boundary',
  };

  // Gets boundaries in format friendly to apis
  self.getApiBounds = function () {
    if (!self.currentBounds) return null;

    const nw = {
      lat: self.currentBounds._northEast.lat,
      lng: self.currentBounds._southWest.lng,
    };
    const se = {
      lat: self.currentBounds._southWest.lat,
      lng: self.currentBounds._northEast.lng,
    };
    return {
      nw,
      se,
    };
  };

  self.setEquipmentResponseData = function (responseData) {
    self.equipmentResponseData = responseData;
  };

  self.setFacets = function (facets) {
    self.facets = facets;
  };

  self.setTagFilters = function (tagFilters) {
    self.tagFilters = tagFilters;
  };

  self.getLimit = function () {
    return 2500;
  };

  self.setCursor = function (cursor) {
    self.cursor = cursor;
  };

  self.getCursor = function () {
    return self.cursor;
  };

  self.getControlLayer = function () {
    return self.controlLayer;
  };

  self.setCurrentBounds = function (bounds) {
    self.currentBounds = bounds;
  };

  self.setRequestedEquipmentId = function (equipmentId) {
    self.requestedEquipmentId = equipmentId;
  };

  self.setSelectedEquipment = function (equipment) {
    self.selectedEquipment = equipment;
  };

  self.setQueryString = function (queryString) {
    self.queryString = queryString;

    let queryStringValue = null;

    if (queryString !== '*') {
      queryStringValue = queryString;
    }
    $location.search('q', queryStringValue);
  };

  self.setSelectedEquipmentFull = function () {
    return self.selectedEquipmentFull;
  };

  self.setVisibleGeozones = function (visibleGeozones) {
    self.visibleGeozones = visibleGeozones;
  };

  self.setSelectedGeozone = function (selectedGeozone) {
    self.selectedGeozone = selectedGeozone;
  };

  self.setFilters = function (newFilters) {
    self.filters = newFilters;
  };

  self.setIsMapLoaded = function (flag) {
    self.isMapLoaded = flag;
  };

  self.getIsMapLoaded = function () {
    return self.isMapLoaded;
  };

  let storedBaseLayer = null;

  self.createMapDiv = function (divName, options) {
    // Arcgis Layers
    const streets = L.esri.basemapLayer('Streets');
    const satellite = L.esri.basemapLayer('Imagery');

    // clear attribution because it fills the screen on mobile
    streets.options.attribution = ' ';
    satellite.options.attribution = ' ';

    // ArcGIS Overlays
    const worldBoundariesAndPlaces = L.esri.basemapLayer('ImageryLabels');
    const transportation = L.esri.basemapLayer('ImageryTransportation');

    if (options == null) {
      options = {
        zoomControl: false,
        worldCopyJump: true,
      };
    }

    let defaultMapView = $rootScope.currentUser.claims.data.mapView;
    if (!defaultMapView) defaultMapView = 'streets';
    if (!storedBaseLayer) storedBaseLayer = defaultMapView;

    // Map
    const map = L.map(divName, options, storedBaseLayer);

    if (storedBaseLayer === 'streets') {
      streets.addTo(map);
    } else {
      satellite.addTo(map);
    }

    self.setIsMapLoaded(true);

    // Add layers that are on at app startup
    worldBoundariesAndPlaces.addTo(map); // defaultOverlay

    // translated controls names
    const satelliteTrans = gettextCatalog.getString('Satellite');
    const streetsTrans = gettextCatalog.getString('Streets');
    const streetNameTrans = gettextCatalog.getString('Street Names');
    const boundaryTrans = gettextCatalog.getString('Boundaries and Places');

    // BaseMap Layer Control
    const baseLayers = {};
    baseLayers[satelliteTrans] = satellite;
    baseLayers[streetsTrans] = streets;

    // BaseMap Overlays
    const overlays = {};
    overlays[streetNameTrans] = transportation;
    overlays[boundaryTrans] = worldBoundariesAndPlaces;

    // Basemap Layer controls
    self.controlLayer = L.control.layers(baseLayers, overlays).addTo(map);

    // add zoom control with your options
    L.control
      .zoom({
        position: 'topright',
        zoomInTitle: gettextCatalog.getString('Zoom in'),
        zoomOutTitle: gettextCatalog.getString('Zoom out'),
      })
      .addTo(map);

    // Set Min/Max Zoom levels
    map.options.maxZoom = 19;
    map.options.minZoom = 3;
    map.options.reset = true;

    // Zoom Home Tool
    const home = L.control
      .defaultExtent({
        position: 'topright',
        title: gettextCatalog.getString('Zoom to Home'),
      })
      .addTo(map);

    const center = L.latLng(40, -74.5); // set home position here
    home.setCenter(center);
    home.setZoom(3); // set home zoom level here

    // Locate Tool
    L.control
      .locate({
        position: 'topright',
        locateOptions: {
          maxZoom: 17,
        },
        showPopup: false,
        watch: false,
        strings: {
          title: gettextCatalog.getString('Show me where I am'),
        },
        icon: 'z-location-icon',
      })
      .addTo(map);

    // Scale

    usersService.getUserClaims().then((result) => {
      const claims = result.data;
      const isImperial = claims.unitOfMeasure === 'imperial';
      L.control
        .scale({
          position: 'bottomleft',
          metric: !isImperial,
          imperial: isImperial,
        })
        .addTo(map);
    });

    // Search
    const arcgisOnline = L.esri.Geocoding.arcgisOnlineProvider();

    // create the geocoding control and add it to the map
    // add searchControl
    const searchControl = L.esri.Geocoding.geosearch({
      providers: [arcgisOnline],
      position: 'topright',
      useMapBounds: false,
      placeholder: gettextCatalog.getString('Search for places or addresses'),
      title: gettextCatalog.getString('Location Search'),
    }).addTo(map);

    searchControl.on('results', () => {
      addToDataLayer({
        event: 'searchLocationFound',
      });
    });

    // create an empty layer group to store the results and add it to the map
    L.layerGroup().addTo(map);

    if (options.weatherEnabled === true) {
      // Add Weather Control
      L.control.weather().addTo(map);
    }

    map.on('baselayerchange', (e) => {
      storedBaseLayer = e.name.toLowerCase();
    });

    return map;
  };

  // Generates an array of geojson objects for leaflet to draw on the map from an array of equiment objects
  self.getEquipmentGeoJson = function (equipmentList) {
    // Filtering invalid coords
    equipmentList = equipmentList.filter((equip) => equip && Array.isArray(equip.coords));

    const equipmentGeoList = [];

    equipmentList.forEach((equipment) => {
      // get lat lng =>first item in coord should be lat and the second lng
      const [lat, lng] = equipment.coords;

      equipment.promoted = true;

      if (L.latLng(lat, lng)) {
        equipmentGeoList.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lat, lng],
          },
          properties: equipment,
        });
      }
    });
    return equipmentGeoList;
  };

  self.generateFeature = function (feature, latlng) {
    const myIcon = L.divIcon({
      className: 'marker',
      iconSize: L.point(22, 50),
      iconAnchor: [0, 24],
      html: '<div></div>',
    });

    const result = L.marker(latlng, {
      icon: myIcon,
      riseOnHover: false,
    });
    return result;
  };

  self.defineMarkerFeature = function (feature, latlng) {
    let className = 'asset-icon';

    let maxPriority = 0;

    const equipment = feature.properties;

    if (equipment && equipment.properties) {
      for (let i = 0; i < equipment.properties.length; i++) {
        const tag = equipment.properties[i];

        if (tag.indexOf('alert:high') !== -1) {
          maxPriority = Math.max(3, maxPriority);
        } else if (tag.indexOf('alert:medium') !== -1) {
          maxPriority = Math.max(2, maxPriority);
        } else if (tag.indexOf('alert:low') !== -1) {
          maxPriority = Math.max(1, maxPriority);
        }
      }
    }

    if (maxPriority === 3) {
      className += ' asset-icon-red icon';
    } else if (maxPriority === 2) {
      className += ' asset-icon-yellow icon';
    } else if (maxPriority === 1) {
      className += ' asset-icon-blue icon';
    } else {
      className += ' asset-icon-green icon';
    }

    const html =
      '<span style="color:white; width: 100%;height: 100%;vertical-align: middle;" class="text-center fa fa-map-marker"></span>';
    const iconSize = 18;

    const result = L.marker(latlng, {
      icon: L.divIcon({
        className,
        html,
        iconSize: [iconSize, iconSize],
        iconAnchor: [iconSize / 2, iconSize / 2],
        popupAnchor: [0, 0],
      }),
      riseOnHover: false,
    });

    return result;
  };

  self.defineClusterIcon = function (cluster) {
    function bakeThePie(options) {
      /* data and valueFunc are required */
      if (!options.data || !options.valueFunc) {
        return '';
      }

      options.strokeWidth = 1.5;

      const outerStrokeWidth = 2;

      const { data } = options;
      const { valueFunc } = options;
      const r = options.outerRadius ? options.outerRadius : 28; // Default outer radius = 28px
      const strokeWidth = options.strokeWidth ? options.strokeWidth : 1; // Default stroke is 1
      const pathClassFunc = options.pathClassFunc
        ? options.pathClassFunc
        : function () {
            return '';
          }; // Class for each path
      // pathTitleFunc = options.pathTitleFunc?options.pathTitleFunc:function(){return '';}, //Title for each path
      const pieClass = options.pieClass ? options.pieClass : 'marker-cluster-pie'; // Class for the whole pie
      const pieLabel = options.pieLabel ? options.pieLabel : d3.sum(data, valueFunc); // Label for the whole pie
      const pieLabelClass = options.pieLabelClass
        ? options.pieLabelClass
        : 'marker-cluster-pie-label'; // Class for the pie label
      const origo = r + strokeWidth + outerStrokeWidth; // Center coordinate
      const w = origo * 2; // width and height of the svg element
      const h = w;
      const donut = d3.layout.pie();
      const arc = d3.svg
        .arc()
        .innerRadius(r - 2)
        .outerRadius(r);

      // Create an svg element
      const svg = document.createElementNS('d3svg', 'svg');
      // Create the pie chart
      const vis = d3
        .select(svg)
        .data([data])
        .attr('class', pieClass)
        .attr('width', w)
        .attr('height', h);

      vis
        .append('circle')
        .attr('cx', origo)
        .attr('cy', origo)
        .attr('r', origo - 3)
        .attr('fill-opacity', '0.7')
        .attr('fill', '#000000');

      vis
        .append('circle')
        .attr('cx', origo)
        .attr('cy', origo)
        .attr('r', origo - 1)
        .attr('fill-opacity', '0.0')
        .attr('stroke', 'white')
        .attr('stroke-width', outerStrokeWidth);

      const arcs = vis
        .selectAll('g.arc')
        .data(donut.value(valueFunc))
        .enter()
        .append('svg:g')
        .attr('class', 'arc')
        .attr('transform', `translate(${origo},${origo})`);

      arcs
        .append('svg:path')
        .attr('class', pathClassFunc)
        .attr('stroke-width', strokeWidth + 1)
        .attr('d', arc)
        .append('svg:title');

      vis
        .append('text')
        .attr('x', origo)
        .attr('y', origo)
        .attr('class', pieLabelClass)
        .attr('text-anchor', 'middle')
        // .attr('dominant-baseline', 'central')
        // IE doesn't seem to support dominant-baseline, but setting dy to .3em does the trick
        .attr('dy', '.3em')
        .text(pieLabel);

      // Return the svg-markup rather than the actual element
      if (typeof window.XMLSerializer !== 'undefined') {
        return new window.XMLSerializer().serializeToString(svg);
      }
      if (typeof svg.xml !== 'undefined') {
        return svg.xml;
      }
      return '';
    }

    const children = cluster.getAllChildMarkers();

    let popupContent = `<div class="asset-popup-list-header">`;
    popupContent += `<label>${gettextCatalog.getString('Assets')}:</label>`;
    popupContent += `</div>`;
    popupContent += `<div class="${
      children.length <= 10
        ? 'map-asset-popup list-bottom-cut'
        : 'map-asset-popup cluster-popup-scroll list-bottom-cut'
    }">`;
    popupContent += `<ul>`;

    children.sort((left, right) => {
      const leftName = left.feature.properties.name.toLowerCase();
      const rightName = right.feature.properties.name.toLowerCase();

      if (leftName < rightName) {
        return -1;
      }
      if (rightName < leftName) {
        return 1;
      }
      return 0;
    });

    children.forEach((el) => {
      /* Capture each asset name / equipment id from each asset. */
      const {
        _latlng: { lat, lng },
        feature: {
          properties: { name: assetName = '', id: equipmentId },
        },
      } = el;

      popupContent += `<li><a id="${equipmentId}" lat="${lat}" lng="${lng}" class="popupItem">${assetName}</a></li>`;
    });

    popupContent += `</ul>`;
    popupContent += `</div>`;

    /* Adjust marker settings and add the pushpin to its container. */
    cluster.bindPopup(popupContent, {
      closeButton: true,
      autoClose: true,
    });

    /* If it is the main map page allow a popup to show on hover over marker. This is
     * only if the popup is not already open. Otherwise, ignore the listener because
     * the current map is not the main one. */
    const mainMap = document.getElementsByClassName('page-map');

    if (mainMap.length > 0) {
      cluster.on('mouseover', function () {
        const leftPanel = document.getElementsByClassName('sidepanel-content-full');

        if (!cluster._popup.isOpen() && leftPanel.length === 0) {
          this.openPopup();

          addToDataLayer({
            event: 'showAssetListPopupOnMap',
            data: {
              totalAssets: children.length,
            },
          });
        }
      });
    }

    cluster.getPopup().on('remove', () => {
      addToDataLayer({
        event: 'assetListPopupClosed',
      });
    });

    const n = children.length; // Get number of markers in cluster
    const rmax = 30; // Maximum radius for cluster pies
    const strokeWidth = 1; // Set clusterpie stroke width
    const r = rmax - 2 * strokeWidth - (n < 10 ? 12 : n < 100 ? 8 : n < 1000 ? 4 : 0); // Calculate clusterpie radius...
    const iconDim = (r + strokeWidth) * 2; // ...and divIcon dimensions (leaflet really want to know the size)
    const data = d3
      .nest() // Build a dataset for the pie chart
      .key(function (d) {
        let category = 'healthy';

        let maxPriority = 0;

        const equipment = d.feature.properties;

        if (equipment && equipment.properties) {
          for (let i = 0; i < equipment.properties.length; i++) {
            const tag = equipment.properties[i];

            if (tag.indexOf('alert:high') !== -1) {
              maxPriority = Math.max(3, maxPriority);
            } else if (tag.indexOf('alert:medium') !== -1) {
              maxPriority = Math.max(2, maxPriority);
            } else if (tag.indexOf('alert:low') !== -1) {
              maxPriority = Math.max(1, maxPriority);
            }
          }
        }

        if (maxPriority === 3) {
          category = 'alarming';
        } else if (maxPriority === 2) {
          category = 'faulting';
        } else if (maxPriority === 1) {
          category = 'low-alarming';
        }

        return category;
      })
      .sortKeys(d3.ascending)
      .entries(children, d3.map);
    // bake some svg markup
    const html = bakeThePie({
      data,
      valueFunc(d) {
        return d.values.length;
      },
      strokeWidth: 1,
      outerRadius: r,
      pieClass: 'cluster-pie',
      pieLabel: n,
      pieLabelClass: 'marker-cluster-pie-label',
      pathClassFunc(d) {
        return `category-${d.data.key}`; // todo: make it dynamic return "category-" + d.data.key;
      },
      // pathTitleFunc: function(d){return metadata.fields[categoryField].lookup[d.data.key]+' ('+d.data.values.length+' accident'+(d.data.values.length!=1?'s':'')+')';}
    });
    // Create a new divIcon and assign the svg markup to the html property
    const myIcon = new L.DivIcon({
      html,
      className: 'marker-cluster',
      iconSize: new L.Point(iconDim, iconDim),
    });
    return myIcon;
  };
}
